import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import TopWinsSection from '../TopWinsSection';
import { useTopWins } from '../../hooks/useTopWins';
import { buildTopWinsList } from '../../utils/buildTopWinsList';
import { DefaultSectionHeader } from 'layouts/Section/SectionHeader';
import type { ISectionProps } from 'types/models/ISectionProps';
import { setSelectedSection, setViewMode } from 'store/app/appStore';
import { sectionNavigationTracker } from 'core/services/SectionNavigationTracker';
import { useGameSounds } from 'shared/hooks';
// Shared module so the `[data-section='TOP_WIN_GAMES']` cascade in
// layouts/Section resolves against the same scoped `.sectionContainer` class.
import styles from 'layouts/Section/Section.module.css';

interface TopWinGamesSectionProps {
  data: ISectionProps['data'];
  isActive: boolean;
  containerProps: Record<string, string>;
}

const TopWinGamesSection: FC<TopWinGamesSectionProps> = ({
  data,
  isActive,
  containerProps,
}) => {
  const { featured, recent } = useTopWins();
  const { playSelectTabSound } = useGameSounds();
  const minLimit = data.minLimit != null && data.minLimit >= 1 ? data.minLimit : 1;
  const isSectionActive = recent.length >= minLimit;

  // Same set/count the single-view section shows, so "All (N)" matches its title.
  const gamesCount = useMemo(() => buildTopWinsList(featured, recent).length, [featured, recent]);

  // Mirror the generic Section's "All" link behaviour — open this section in
  // single view. (In multi view isActive is false, so the guard passes.)
  const handleViewToggle = useCallback(() => {
    if (isActive) return;
    playSelectTabSound();
    sectionNavigationTracker.trackSectionNavigation(data.name, 'all_link');
    setSelectedSection(data.name);
    setViewMode('single');
  }, [isActive, data.name, playSelectTabSound]);

  if (!isSectionActive) return null;

  return (
    <div className={styles.sectionContainer} {...containerProps}>
      <DefaultSectionHeader
        name={data.name}
        isActive={isActive}
        gamesCount={gamesCount}
        handleViewToggle={handleViewToggle}
      />
      <div className={styles.sectionBody}>
        <TopWinsSection />
      </div>
    </div>
  );
};

export default TopWinGamesSection;
